

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import { fetchCustomerConsolidatedReport } from "../../redux/actions/customerAction";
import { getBanners } from "../../redux/actions/bannerActions";
import { FullScreenLoader } from "../Components/Loader";
import dayjs from "dayjs";
import {
  FiCheckCircle,
  FiTrendingUp,
  FiGift,
  FiList,
} from "react-icons/fi";

// ── Banners Carousel ───────────────────────────────────────────────────────
const BannersCarousel = ({ banners }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (!banners || banners.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % banners.length;
        if (carouselRef.current) {
          carouselRef.current.scrollTo({
            left: nextIndex * carouselRef.current.offsetWidth,
            behavior: "smooth",
          });
        }
        return nextIndex;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [banners]);

  if (!banners || banners.length === 0) return null;

  return (
    <div className="relative w-full overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl bg-white border border-gray-200 shadow-md">
      <div
        ref={carouselRef}
        className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {banners.map((banner, index) => (
          <div key={banner.id || index} className="flex-shrink-0 w-full snap-center">
            <div className="block md:hidden w-full relative">
              <img
                src={banner.url_link || banner.image || "https://via.placeholder.com/400x300?text=Offer"}
                alt={banner.banner_type || banner.name || "Offer"}
                className="w-full h-auto max-h-72 object-cover"
                onError={(e) => { e.target.src = "https://via.placeholder.com/400x300?text=Image+Not+Available"; }}
              />
            </div>
            <div className="hidden md:flex flex-row items-center justify-between h-48 gap-6 p-6">
              <div className="flex-shrink-0 w-2/5 h-full">
                <img
                  src={banner.url_link || banner.image || "https://via.placeholder.com/400x300?text=Offer"}
                  alt={banner.banner_type || banner.name || "Offer"}
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => { e.target.src = "https://via.placeholder.com/400x300?text=Image+Not+Available"; }}
                />
              </div>
              <div className="flex-1" />
            </div>
          </div>
        ))}
      </div>
      {banners.length > 1 && (
        <div className="absolute bottom-3 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black/40 rounded-full px-3 py-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                if (carouselRef.current) {
                  carouselRef.current.scrollTo({
                    left: index * carouselRef.current.offsetWidth,
                    behavior: "smooth",
                  });
                }
              }}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? "bg-white w-8" : "bg-gray-400 hover:bg-gray-300 w-2"
              }`}
              aria-label={`Go to banner ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Status badge helper ────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    redeemed_and_closed: { label: "Earned", cls: "bg-yellow-100 text-yellow-700" },
    accumulated:         { label: "Accumulated", cls: "bg-green-100 text-green-700" },
    pending:             { label: "Pending", cls: "bg-red-100 text-red-700" },
  };
  const { label, cls } = map[status] || { label: status, cls: "bg-gray-100 text-gray-700" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${cls}`}>
      {label}
    </span>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
const CustomerDashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userInfo } = useSelector((state) => state.auth);
  const dashboardData = useSelector((state) => state.dashboard?.data);
  const { consolidatedReport, reportLoading, reportError } = useSelector((state) => state.customer || {});
  const { banners, loading: bannersLoading } = useSelector((state) => state.banner || {});

  const customerId = dashboardData?.customer_id || userInfo?.customer_id;
  const customerLoginType = dashboardData?.customer_login_type || userInfo?.customer_login_type;
  const isPromotional = customerLoginType === "promotional";

  useEffect(() => {
    if (customerId && userInfo?.access_token) fetchReport();
  }, [customerId, userInfo?.access_token]);

  useEffect(() => {
    if (userInfo?.access_token) {
      dispatch(getBanners({ role_id: 4, is_live: true }, userInfo.access_token));
    }
  }, [dispatch, userInfo?.access_token]);

  const fetchReport = async () => {
    if (!customerId || !userInfo?.access_token) return;
    await dispatch(fetchCustomerConsolidatedReport({ customer_id: customerId }, userInfo.access_token));
  };

  const summary = consolidatedReport?.summary || {};
  const details = consolidatedReport?.details || {};
  const customerInfo = consolidatedReport?.customer || {};

  const now = dayjs();
  const filteredBanners = Array.isArray(banners)
    ? banners.filter((banner) => {
        if (banner.is_live !== true) return false;
        if (banner.role_ids?.length > 0) {
          if (!banner.role_ids.includes(4) && !banner.role_ids.includes("4")) return false;
        }
        if (banner.start_date && now.isBefore(dayjs(banner.start_date))) return false;
        if (banner.end_date && now.isAfter(dayjs(banner.end_date))) return false;
        return true;
      })
    : [];

  return (
    <DashboardLayout>
      <FullScreenLoader
        show={reportLoading || bannersLoading}
        message={t("dashboard.loadingDashboard")}
        variant="primary"
        blurIntensity="md"
      />

      <div className="max-w-full mx-auto px-2 sm:px-4 py-3 sm:py-4 md:py-6">
        {/* Header */}
        <div className="mb-3 sm:mb-4 md:mb-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-1 sm:mb-2">
            {t("dashboard.customerDashboard")}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            {customerInfo?.name && (
              <p className="text-gray-600 text-xs sm:text-sm md:text-base">
                {t("dashboard.welcome")}, <span className="font-semibold">{customerInfo.name}</span>
              </p>
            )}
            {dashboardData?.customer_code && (
              <span className="text-gray-500 text-xs sm:text-sm md:text-base">
                ({dashboardData.customer_code})
              </span>
            )}
          </div>
        </div>

        {/* Error */}
        {reportError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <p className="text-red-700 text-xs sm:text-sm">
              <strong>{t("dashboard.error")}:</strong> {reportError?.message || t("dashboard.failedToLoad")}
            </p>
          </div>
        )}

        {/* Banners */}
        {filteredBanners.length > 0 && (
          <div className="mb-4 sm:mb-6">
            <BannersCarousel banners={filteredBanners} />
          </div>
        )}

        {/* ── PROMOTIONAL SUMMARY ── */}
        {consolidatedReport && isPromotional && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            {/* QR Codes Scanned */}
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">QR Codes Scanned</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600">
                    {summary.total_qr_codes_redeemed ?? 0}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                  <FiList className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                </div>
              </div>
            </div>

            {/* Pending Points */}
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Pending Points</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600">
                    {summary.total_points_accumulated ?? 0}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                  <FiCheckCircle className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                </div>
              </div>
            </div>

            {/* Total Points Earned */}
            <div className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Total Points Earned</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-600">
                    {summary.total_points_redeemed ?? 0}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                  <FiTrendingUp className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                </div>
              </div>
            </div>

            {/* Gifts Redeemed */}
            <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{t("dashboard.giftsRedeemed")}</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-600">
                    {summary.total_gifts_redeemed ?? 0}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-amber-400 to-amber-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                  <FiGift className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── LOYALTY SUMMARY ── */}
        {consolidatedReport && !isPromotional && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{t("dashboard.qrCodesAccumulated")}</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600">
                    {summary.total_qr_codes_accumulated ?? 0}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                  <FiCheckCircle className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{t("dashboard.qrCodesRedeemed")}</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-600">
                    {summary.total_qr_codes_redeemed ?? 0}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                  <FiTrendingUp className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{t("dashboard.giftsRedeemed")}</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-600">
                    {summary.total_gifts_redeemed ?? 0}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-amber-400 to-amber-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                  <FiGift className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── DETAILS ── */}
        {consolidatedReport && (
          <div className="space-y-4 sm:space-y-6">

            {/* ── PROMOTIONAL: QR Code History ── */}
            {isPromotional && details.qr_code_history && details.qr_code_history.length > 0 && (
              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg border-2 border-green-200 p-3 sm:p-4 md:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                  <FiList className="text-green-600 flex-shrink-0" size={20} />
                  <span>QR Code History ({details.qr_code_history.length})</span>
                </h2>
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full bg-white text-xs sm:text-sm">
                      <thead className="bg-green-500 text-white">
                        <tr>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">QR ID</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">Scheme</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-center">Points</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-center">Status</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {details.qr_code_history.map((qr) => (
                          <tr key={qr.id} className="hover:bg-green-50">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium break-all">{qr.qr_id || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">{qr.qr_scheme || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-bold text-green-600">
                              {qr.qr_amount ?? "-"}
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-center text-md">
                              <StatusBadge status={qr.status} />
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              {qr.created_at ? dayjs(qr.created_at).format("DD MMM YYYY") : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── LOYALTY: Accumulated QR Codes ── */}
            {!isPromotional && details.accumulated_qr_codes && details.accumulated_qr_codes.length > 0 && (
              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg border-2 border-green-200 p-3 sm:p-4 md:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                  <FiCheckCircle className="text-green-600 flex-shrink-0" size={20} />
                  <span>{t("dashboard.accumulatedQRCodes")} ({details.accumulated_qr_codes.length})</span>
                </h2>
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full bg-white text-xs sm:text-sm">
                      <thead className="bg-green-500 text-white">
                        <tr>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">QR ID</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">{t("dashboard.scheme")}</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">{t("dashboard.date")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {details.accumulated_qr_codes.slice(0, 10).map((qr) => (
                          <tr key={qr.id} className="hover:bg-green-50">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium break-all">{qr.qr_id || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">{qr.qr_scheme || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              {qr.created_at ? dayjs(qr.created_at).format("DD MMM YYYY") : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── LOYALTY: Redeemed QR Codes ── */}
            {!isPromotional && details.redeemed_qr_codes && details.redeemed_qr_codes.length > 0 && (
              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg border-2 border-purple-200 p-3 sm:p-4 md:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                  <FiTrendingUp className="text-purple-600 flex-shrink-0" size={20} />
                  <span>{t("dashboard.redeemedQRCodes")} ({details.redeemed_qr_codes.length})</span>
                </h2>
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full bg-white text-xs sm:text-sm">
                      <thead className="bg-purple-500 text-white">
                        <tr>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">QR ID</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">{t("dashboard.scheme")}</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">{t("dashboard.date")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {details.redeemed_qr_codes.slice(0, 10).map((qr) => (
                          <tr key={qr.id} className="hover:bg-purple-50">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium break-all">{qr.qr_id || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">{qr.qr_scheme || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              {qr.redeemed_at ? dayjs(qr.redeemed_at).format("DD MMM YYYY") : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── GIFTS REDEEMED (both types) ── */}
            {details.gifts_redeemed && details.gifts_redeemed.length > 0 && (
              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg border-2 border-amber-200 p-3 sm:p-4 md:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                  <FiGift className="text-amber-600 flex-shrink-0" size={20} />
                  <span>{t("dashboard.giftsRedeemedTitle")} ({details.gifts_redeemed.length})</span>
                </h2>
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full bg-white text-xs sm:text-sm">
                      <thead className="bg-amber-500 text-white">
                        <tr>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">{t("dashboard.giftName")}</th>
                          {isPromotional && (
                            <th className="py-2 sm:py-3 px-2 sm:px-4 text-center">Points Required</th>
                          )}
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">{t("dashboard.scheme")}</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">Redeemed By</th>
                          <th className="py-2 sm:py-3 px-2 sm:px-4 text-left">{t("dashboard.date")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {details.gifts_redeemed.slice(0, 10).map((gift) => (
                          <tr key={gift.id} className="hover:bg-amber-50">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium">
                              <div className="flex items-center gap-2">
                                {isPromotional && gift.gift_image && (
                                  <img
                                    src={gift.gift_image}
                                    alt={gift.gift_name}
                                    className="w-8 h-8 object-cover rounded border border-gray-200 flex-shrink-0"
                                    onError={(e) => { e.target.style.display = "none"; }}
                                  />
                                )}
                                {gift.gift_name || "-"}
                              </div>
                            </td>
                            {isPromotional && (
                              <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-bold text-amber-700">
                                {gift.qr_count_required ?? "-"}
                              </td>
                            )}
                            <td className="py-2 sm:py-3 px-2 sm:px-4">{gift.qr_scheme || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">{gift.redeemed_by_name || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4">
                              {gift.created_at ? dayjs(gift.created_at).format("DD MMM YYYY") : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!reportLoading && !reportError && consolidatedReport &&
              (!details.accumulated_qr_codes || details.accumulated_qr_codes.length === 0) &&
              (!details.redeemed_qr_codes || details.redeemed_qr_codes.length === 0) &&
              (!details.gifts_redeemed || details.gifts_redeemed.length === 0) &&
              (!details.qr_code_history || details.qr_code_history.length === 0) && (
                <div className="bg-white rounded-lg sm:rounded-xl shadow-lg border-2 border-gray-200 p-6 sm:p-8 md:p-12 text-center">
                  <FiTrendingUp className="mx-auto text-gray-400 text-4xl sm:text-5xl mb-4" />
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">{t("dashboard.noDataAvailable")}</h3>
                  <p className="text-sm sm:text-base text-gray-600">{t("dashboard.noActivityData")}</p>
                </div>
              )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CustomerDashboard;